import _ from "lodash";
import { v4 } from 'uuid';
import { DomUtils, MoModel, MoUtils, QueryStore, QueryStoreConfig } from "ui-common";
import { cacheBeaconConfig, clearStoredCartId, facetsConfigIsValid, fetchBeaconConfig, getBeaconConfig, getCartId, getFacets, getFieldValue, getSavedBeaconConfig, isOptionalProductProp, isRegExpPattern, searchPageType, unmatchedPageType } from "../beacon/beacon-helper";
import { BeaconAgentData, BeaconAgentSetupOptions, BeaconConfig, BeaconFieldConfig, MergedBeaconConfig, RetailQueryStoreConfig, SavedBeaconConfig, SignalType } from "./interfaces";
import { EmbedsConfigApiService } from "../embeds-config-api-service";
import { configureStoreApiService } from "../embeds-store-api-helper";
import { RetailQueryStore } from "../../stores/RetailQueryStore";
import { RetailCartAddSignalRequest, RetailClickSignalRequest, RetailProduct, RetailProductDetail, RetailPurchaseSignalRequest, RetailQuerySignalRequest } from "../interfaces";

export class BeaconAgent extends MoModel {
  public beaconConfig: MergedBeaconConfig;
  public querySignalRequestDebounce: number = 200;
  private customSignalHandlers: {[K in SignalType]?: (...args: any[]) => any } = {};
  private debouncedQuerySignal: _.DebouncedFunc<(query: string) => void>;
  public defaultLocalApiUrl: string = 'http://localhost:27016/v1';
  public localModeKey: string = 'lwBeaconLocalMode';
  public localApiUrlKey: string = 'lwBeaconLocalApiUrl';
  public localCustomerIdKey: string = 'lwBeaconLocalCustomerId';

  private siteSearchQueryStore: QueryStore;
  private retailQueryStore: RetailQueryStore;
  private configApiService: EmbedsConfigApiService;
  private isRetail: boolean = false;
  private isLocalMode: boolean = false;
  private logVerboseInfo: boolean = localStorage.enableBeaconDev === 'verbose';
  private initialConfig: BeaconConfig;
  private getEmbedQueryId: () => string;

  constructor(data: BeaconAgentData = {}) {
    super(data, {});
    this.debouncedQuerySignal = _.debounce((query: string) => {
      if (this.isQueryTrackingAllowed()) {
        this.doSendQuerySignal(query);
      }
    }, this.querySignalRequestDebounce);

    Object.defineProperty(window, 'useLocalBeaconConfig', {
      value: () => {
        const localConfig = JSON.parse(localStorage.getItem('lwBeaconConfig') || '{}');

        if (!localConfig.config && this.logVerboseInfo) {
          console.warn('Cannot use local beacon config: config not found in local storage.');
          return;
        }

        this.beaconConfig = getBeaconConfig({
          ...this.initialConfig,
          ...(_.isEmpty(localConfig?.config) ? {} : localConfig.config),
        })
        this.startUpBeacon();
      },
      writable: false,
      configurable: true,
    })

    Object.defineProperty(window, 'refreshBeaconConfig', {
      value: async () => {
        const savedConfig = await fetchBeaconConfig(this.configApiService);
        if (savedConfig) {
          cacheBeaconConfig(savedConfig);
        }

        this.beaconConfig = getBeaconConfig({
          ...this.initialConfig,
          ...(_.isEmpty(savedConfig?.config) ? {} : savedConfig.config),
        })
      },
      writable: false,
      configurable: true
    });

    Object.defineProperty(window, 'addBeaconListener', {
      value: (signalType: SignalType, signalHandler: (...args: any[]) => any) => {
        if (typeof signalType !== 'string') {
          if (this.logVerboseInfo) {
            console.warn('"addBeaconListener" signal type must be a string');
          }
          return;
        }
        
        const validSignalTypes: SignalType[] = ['query', 'click', 'cartAdd', 'purchase'];
        if (!validSignalTypes.includes(signalType as SignalType)) {
          if (this.logVerboseInfo) {
            console.warn(`"addBeaconListener" signal type must be one of: ${validSignalTypes.join(', ')}`);
          }
          return;
        }
        
        if (typeof signalHandler !== 'function') {
          if (this.logVerboseInfo) {
            console.warn('"addBeaconListener" signal handler must be a function');
          }
          return;
        }
        this.customSignalHandlers[signalType] = signalHandler;
      },
      writable: false,
      configurable: true
    });
  }

  async setup(config: BeaconConfig, { getEmbedQueryId = () => '' } : BeaconAgentSetupOptions = {}) {
    if (config) {
      this.initialConfig = config;
      const { apiUrl, embedToken } = config.api || {};
      let savedConfig: SavedBeaconConfig = {};

      this.configApiService = new EmbedsConfigApiService({ apiUrl, embedToken });

      savedConfig = await getSavedBeaconConfig(this.configApiService);

      this.beaconConfig = getBeaconConfig({
        ...config,
        ...(_.isEmpty(savedConfig?.config) ? {} : savedConfig.config),
      });

      if (this.debouncedQuerySignal) {
        this.debouncedQuerySignal = _.debounce((query: string) => {
          if (this.isQueryTrackingAllowed()) {
            this.doSendQuerySignal(query);
          }
        }, this.beaconConfig.query.sendSignalDebounceDelay);
      }

      this.isRetail = _.toLower(config.type) !== 'site_search';
      this.isLocalMode = localStorage[this.localModeKey] === 'true';
      this.getEmbedQueryId = getEmbedQueryId;

      if (savedConfig?.enabled !== false) {
        this.startUpBeacon();
      }

      if (this.beaconConfig?.visit?.enabled) {
        this.sendSiteVisitSignal();
      }
    }
  }

  startUpBeacon() {
    /**
     * [Bulk Request]: Instantiate query store right away and flush the potential pending requests.
     */
    const bulkRequest = this.isRetail && this.beaconConfig.attributes?.bulkSignalsInterval > 0;

    if (bulkRequest) {
      this.getRetailQueryStore();
      this.retailQueryStore.bulkRequest();
    }

    /**
     * [Deep Linking]: Monitor query changes resulting from URL query parameter updates.
     */
    this.trackQueryChanges();
    MoUtils.trackHistoryStateChange(() => this.trackQueryChanges());

    this.checkCategoryQuery();

    /**
     * [custom Query Elements]: Monitor query changes in search-related elements, such as the search input, button, and icon.
     */
    this.watchQueryTriggers();

    // Ensures this handler executes before the registered listeners from deeper elements in the DOM.
    document.addEventListener(`click`, (event) => this.onPageClick(event), { capture: true });

    this.checkDevSetup();
  }

  isQueryTrackingAllowed(): boolean {
    let allowUrls = this.beaconConfig.query?.allowUrls;
    let isAllowed = _.isEmpty(allowUrls);

    if (!isAllowed) {
      const windowTop = MoUtils.getWindowTop();
      const url = windowTop.document.location.href;

      allowUrls = _.isArray(allowUrls) ? allowUrls : [];
      isAllowed = _.some(allowUrls, (allowedUrl) => _.includes(url, allowedUrl));
    }

    return isAllowed;
  }

  watchQueryTriggers() {
    if (this.beaconConfig.query.trigger) {
      const queryTriggers: Element[] = [];
      let queryInput;

      _.each(document.querySelectorAll(this.beaconConfig.query.trigger), (trigger) => {
        if (trigger.tagName === 'INPUT') {
          queryInput = trigger;
        } else {
          queryTriggers.push(trigger);
        }
      });

      if (queryInput) {
        const delaySendQuerySignals = async() => {
          await MoUtils.waitFor(this.beaconConfig.query.delayForTrackQueryUpdate);
          this.sendQuerySignal(queryInput.value);
        };

        if (queryInput.value) {
          delaySendQuerySignals();
        }

        // Fire query signals when hitting enter on search input.
        queryInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            delaySendQuerySignals();
          }
        });

        // Fire query signals when clicking on the rest of trigger elements (e.g. search button/icon.)
        _.each(queryTriggers, (element) => {
          element.addEventListener('click', () => delaySendQuerySignals());
        });
      }
    }
  }

  async trackQueryChanges() {
    const queryParams = _.isArray(this.beaconConfig.query.queryParam) 
      ? this.beaconConfig.query.queryParam 
      : [this.beaconConfig.query.queryParam];
    
    const query = _.reduce(queryParams, (result, param) => {
      return result || MoUtils.getUrlQueryParams(param);
    }, null);
    
    const urlHasQuery = !!query;

    const url = MoUtils.getWindowTop().document.location.href;
    const purchaseCompleteConfig = this.beaconConfig?.purchase_complete;

    if (purchaseCompleteConfig && purchaseCompleteConfig?.enabled !== false && _.includes(url, purchaseCompleteConfig?.urlPattern)) {
      await MoUtils.waitFor(purchaseCompleteConfig.delayForPurchaseRedirect);
      this.sendRetailPurchaseSignal(true);
    }

    // REVIEW: Should we also track query with "*:*"?
    if (urlHasQuery) {
      // Delay sending Query Signals to that it will get the most recent query Id if the query is performed via embeds components with deep-linking.
      // REVIEW: A potential follow-up would be further QueryStore support for letting external watch the ongoing request and react after getting response back.
      await MoUtils.waitFor(this.beaconConfig.query.delayForTrackQueryUpdate);

      this.sendQuerySignal(query);
    }
  }

  getPageType(pageHasProducts: boolean) {
    const pageTypesConfig = this.beaconConfig.query.pageTypes;
    const url = MoUtils.getWindowTop().document.location.href;

    const pageType = _.toUpper(_.findKey(pageTypesConfig, (patterns) => {
      return _.some(patterns, (pattern) => {
        return (isRegExpPattern(pattern) && url.match(pattern.replace(/(^\/|\/$)/g, '')) || url === pattern);
      });
    }));

    return pageType || (pageHasProducts ? searchPageType : unmatchedPageType);
  }

  async checkCategoryQuery() {
    const categoryPageQueryMap = this.beaconConfig.query.categoryPageQueryMap;

    if (categoryPageQueryMap) {
      const url = MoUtils.getWindowTop().document.location.href;
      let categoryQuery: string;

      _.some(categoryPageQueryMap, (query, keyword) => {
        const match = _.includes(url, keyword);

        categoryQuery = match ? query : null;

        return match;
      });

      if (categoryQuery) {
        this.sendQuerySignal(categoryQuery);
      }
    }
  }

  sendSiteVisitSignal() {
    this.getRetailQueryStore(true).siteVisitSignal({
      uri: MoUtils.getWindowTop().location.href,
    });

    if (this.logVerboseInfo) {
      console.log('👀 Site Visit Signal Sent:\n\nURL:', MoUtils.getWindowTop().location.href);
    }
  }

  sendQuerySignal(query: string) {
    this.debouncedQuerySignal(query);
  }

  getEnrichedPayload = (signalType: SignalType, payload: any): false | RetailQuerySignalRequest | RetailClickSignalRequest | RetailCartAddSignalRequest | RetailPurchaseSignalRequest => {
    const payloadClone = _.cloneDeep(payload);
    let enrichedPayload: any;
    try {
      enrichedPayload = this.customSignalHandlers[signalType](payloadClone);
    } catch (e) {
      if (this.logVerboseInfo) {
        console.error(`Error in custom ${signalType} signal handler. Original signal will be sent instead.`, e);
      }
    }
    if (enrichedPayload) {
      return enrichedPayload;
    }
    if (enrichedPayload === false) {
      if (this.logVerboseInfo) {
        console.log(`${_.capitalize(signalType)} signal sending stopped by custom signal handler`);
      }
      payload = false;
    }
    return payload;
  };

  doSendQuerySignal(query: string) {
    const products = document.querySelectorAll(this.getProductSelector());
    const searchQuery = query || null;
    const props = { searchTerms: searchQuery } as RetailQuerySignalRequest;

    if (this.isRetail) {
      if (this.beaconConfig.query.facets && facetsConfigIsValid(this.beaconConfig, this.logVerboseInfo)) {
        const facets = getFacets(this.beaconConfig, this.logVerboseInfo);
        if(!_.isEmpty(facets)) {
          props.selected_facets = facets;
        }
      } 

      const productDetails = _.map(products, (product) => this.getRetailProductDetails(product, this.beaconConfig.query.fields));

      const pageTypesConfig = this.beaconConfig.query.pageTypes;
      if(pageTypesConfig) {
        const pageHasProducts = !_.isEmpty(productDetails); 
        const pageType = this.getPageType(pageHasProducts);
        if (pageType !== searchPageType) {
          props.pageType = pageType;
        } 
      }

      let payload: RetailQuerySignalRequest | boolean = {
        ...props,
        productDetails: this.filterProductDetails(productDetails),
      }

      if (this.customSignalHandlers.query) {
        payload = this.getEnrichedPayload('query', payload) as RetailQuerySignalRequest | false;
        if (!payload) {
          return;
        }
      }

      this.getRetailQueryStore(true).querySignal(payload as RetailQuerySignalRequest);

      if (this.logVerboseInfo) {
        const pageType = _.get(payload, 'pageType');
        const facets = _.get(payload, 'selected_facets');
        console.log('🔍 Query Signal Sent:\n\nProduct Details:', (payload as RetailQuerySignalRequest).productDetails, '\nQuery:', (payload as RetailQuerySignalRequest).searchTerms, pageType ? `\nPage Type: ${pageType}` : '', facets ? `\nFacets:` : '', facets ? facets : ''); 
      }
    } else {
      this.getSiteSearchQueryStore(true).querySignal({
        documentIds: _.map(products, (product) => this.getProductId(product)),
        ...props,
      });
    }
  }

  isSignalTypeTrigger(element: HTMLElement, signalType: string) {
    const trigger = this.beaconConfig[signalType]?.trigger;
    const triggerElements = document.querySelectorAll(trigger);
    return _.includes(triggerElements, element) || DomUtils.selectParent(element, trigger);
  }

  onPageClick(event) {
    const element = event.target;
    const isClickTrigger = this.isSignalTypeTrigger(element, 'click');

    if (isClickTrigger || !this.beaconConfig.click?.trigger) {
      this.trySendingClickSignal(element);
    }

    if (!this.isRetail) {
      return;
    }

    if (this.isRetail) {
      const isCartAddTrigger = this.isSignalTypeTrigger(element, 'cartAdd');
      const isPurchaseTrigger = this.isSignalTypeTrigger(element, 'purchase');

      if (isCartAddTrigger) {
        this.sendRetailCartAddSignal(element);
      } else if (isPurchaseTrigger) {
        if (_.get(this.beaconConfig, 'purchase.enabled', true)) {
          this.sendRetailPurchaseSignal(false);
        }
      } 
    }
  }

  filterProductDetails(productDetails: RetailProductDetail[]) {
    return _.filter(productDetails, 'product.productId');
  }

  getProductSelector(path?: string) {
    return path || this.beaconConfig.attributes.productPath;
  }

  sendRetailPurchaseSignal(completePurchase: boolean) {
    const purchaseConfig: any = completePurchase ? this.beaconConfig.purchase_complete : this.beaconConfig.purchase;
    const items = document.querySelectorAll(this.getProductSelector(purchaseConfig.productPath));
    let productDetails = _.map(items, (product) => this.getRetailProductDetails(product, purchaseConfig.fields));
    let revenue: number;
    let orderId: string;

    if (completePurchase) {
      if (purchaseConfig?.orderIdUrlParam) {
        orderId = MoUtils.getUrlQueryParams(purchaseConfig.orderIdUrlParam);
      } else if(purchaseConfig?.orderIdSelector) {
        orderId = this.getValueFromPathConfig(document, purchaseConfig.orderIdSelector, 'orderId');
      }
    }

    productDetails = this.filterProductDetails(productDetails);

    if (!_.isEmpty(productDetails)) {
      if (purchaseConfig.revenue) {
        revenue = this.getValueFromPathConfig(document, purchaseConfig.revenue, 'revenue');
      }

      // Make it default to none zero number just for guarding the error on dev env which should be fine with the retail app.
      revenue ||= _.sumBy(productDetails, (item) => (item.quantity || 1) * (item.product.price || 0)) || 0.1;

      let payload: RetailPurchaseSignalRequest | false = {
        productDetails,
        revenue,
        orderId,
        cartId: getCartId(this.beaconConfig),
      };

      if (this.customSignalHandlers.purchase) {
        payload = this.getEnrichedPayload('purchase', payload) as RetailPurchaseSignalRequest | false;
        if (!payload) {
          return;
        }
      }

      this.sendPurchaseSignal({
        ...payload as RetailPurchaseSignalRequest,
        orderId: (payload as RetailPurchaseSignalRequest).orderId || '' // Ensure orderId is provided, defaulting to an empty string if undefined
      });
    }
  }

  sendRetailCartAddSignal(element) {
    const productElement = this.getProductFromParent(element, this.beaconConfig.cartAdd.productPath);

    if (productElement) {
      const productDetail = this.getRetailProductDetails(productElement, this.beaconConfig.cartAdd.fields);
      const idFromUrl = MoUtils.getUrlQueryParams(this.beaconConfig.attributes.idQueryParam);
      const uri = MoUtils.getURI();

      productDetail.product.productId ||= idFromUrl || uri;
      productDetail.product.uri ||= uri;

      let payload: RetailCartAddSignalRequest | false = {
        productDetails: [productDetail],
        cartId: getCartId(this.beaconConfig),
      }

      if (this.customSignalHandlers.cartAdd) {
        payload = this.getEnrichedPayload('cartAdd', payload) as RetailCartAddSignalRequest | false;
        if (!payload) {
          return;
        }
      }

      this.getRetailQueryStore().cartAddSignal(payload as RetailCartAddSignalRequest);

      if (this.logVerboseInfo) {
        console.log('🛒 Cart Add Signal Sent:\n\nProduct Details:', productDetail, '\nElement:', element);
      }
    }
  }

  async sendPurchaseSignal({ productDetails, revenue, cartId, orderId }) {
    await this.getRetailQueryStore().purchaseSignal({
      productDetails,
      revenue,
      cartId,
      ...(orderId && { orderId: orderId })
    });

    clearStoredCartId(this.beaconConfig);

    if (this.logVerboseInfo) {
      console.log('🛒 Purchase Signal Sent:\n\nProduct Details:', productDetails, '\nrevenue:', revenue, orderId ? `\nOrder ID: ${orderId}` : '');
    }
  }

  trySendingClickSignal(element) {
    const productPath = this.beaconConfig.click.productPath;
    const products = document.querySelectorAll(this.getProductSelector(productPath));
    const elementIsProduct = _.includes(products, element);
    let product;
    let id;

    product = elementIsProduct ? element : this.getProductFromParent(element, productPath);

    if (product) {
      const fields: any = this.getRetailProductDetails(product);

      id = fields?.product?.productId;
      element = product;
    }

    if (id) {
      if (this.isRetail) {
        const selectedPosition = _.indexOf(products, element) + 1;
        let payload: RetailClickSignalRequest | false = { productDetails: [this.getRetailProductDetails(element, this.beaconConfig.click.fields)], selectedPosition };
        if (this.customSignalHandlers.click) {
          payload = this.getEnrichedPayload('click', payload);
          if (!payload) {
            return;
          }
        }

        if (selectedPosition > 0) {
          (payload as RetailClickSignalRequest).selectedPosition = selectedPosition;
        }

        this.getRetailQueryStore().clickSignal(payload as RetailClickSignalRequest);

        if (this.logVerboseInfo) {
          console.log('🖱️ Click Signal Sent:\n\nRequest:', payload, '\nElement:', element);
        }
      } else {
        this.getSiteSearchQueryStore().clickSignal({ documentId: id });
      }
    }
  }

  getProductFromParent(element, productPath?) {
    return DomUtils.selectParent(element, this.getProductSelector(productPath));
  }

  getProductId(element, path: BeaconFieldConfig = this.beaconConfig.fields.productId) {
    return this.getValueFromPathConfig(element, path);
  }

  getValueFromPathConfig(element, fieldConfig, field?) {
    const pathConfig = _.isPlainObject(fieldConfig) ? fieldConfig.path : fieldConfig;
    const paths = (pathConfig || '').split(',').map(_.trim);
    let value;

    _.some(_.compact(paths), (path) => value = getFieldValue(element, path, field, fieldConfig, this.beaconConfig));

    return value;
  }

  getRetailProductDetails(element, fieldsConfig = this.beaconConfig.fields): RetailProductDetail {
    const product: RetailProduct = _.mapValues(fieldsConfig, (fieldPath, field) => this.getValueFromPathConfig(element, fieldPath, field));
    const quantity = product.quantity;

    const productDetail: RetailProductDetail = {
      product: _.omitBy(product, (value, key) => {
        return (!value && isOptionalProductProp(key)) || _.isNaN(value);
      }),
    }

    if (quantity) {
      productDetail.quantity = quantity;
      delete productDetail.product.quantity;
    }

    return productDetail;
  }

  getQueryStore<T extends QueryStore>(storeClass: new (config: QueryStoreConfig) => T, newQuery: boolean = false): T {
    let store: T;
  
    const { apiUrl, embedToken } = this.beaconConfig.api;
    const configureStoreApiServiceOptions = { apiUrl, embedToken, headers: null };
    let apiService;

    if (this.isLocalMode) {
      const customerId = localStorage[this.localCustomerIdKey];

      if (customerId) {
        configureStoreApiServiceOptions.apiUrl = localStorage[this.localApiUrlKey] || this.defaultLocalApiUrl;

        configureStoreApiServiceOptions.headers = {
          'x-lw-customer-uuid': customerId,
          'x-lw-app-uuid': EmbedsConfigApiService.extractAppIdFromAppPath(apiUrl || ''),
        };
      }
    }

    apiService = configureStoreApiService(configureStoreApiServiceOptions, false);
  
    const storeConfig: RetailQueryStoreConfig = {
      type: 'search',
      bulkSignalsInterval: this.beaconConfig.attributes?.bulkSignalsInterval || 0,
      beaconConfig: this.beaconConfig,
    };
  
    store = new storeClass(storeConfig);
    store.getApiService = () => apiService;
  
    if (newQuery || !store.meta.currentParams.queryId) {
      store.setQueryId(this.getEmbedQueryId() ?? v4());
      this.syncUpQueryId(store);
    }
  
    return store;
  }

  getSiteSearchQueryStore(newQuery: boolean = false, force: boolean = false): QueryStore {
    if (!this.siteSearchQueryStore || force) {
      this.siteSearchQueryStore = this.getQueryStore(QueryStore, newQuery);
    }

    return this.siteSearchQueryStore;
  }

  getRetailQueryStore(newQuery: boolean = false, force: boolean = false): RetailQueryStore {
    if (this.retailQueryStore && newQuery) {
      this.syncUpQueryId(this.retailQueryStore);
    }
    
    if (!this.retailQueryStore || force) {
      this.retailQueryStore = this.getQueryStore(RetailQueryStore, newQuery);
    }

    return this.retailQueryStore;
  }

  syncUpQueryId(store: QueryStore) {
    store.setQueryId(this.getEmbedQueryId() ?? v4());
  }

  checkDevSetup() {
    if (this.logVerboseInfo || localStorage.enableBeaconDev === 'true') {
      this.logInfo();
      window.logBeaconInfo = () => this.logInfo();
    }
  }


  logInfo() {
    const queryProducts = document.querySelectorAll(this.getProductSelector());
    const cartAddElements = document.querySelectorAll(this.beaconConfig.cartAdd?.trigger || '');
    const purchaseElements = document.querySelectorAll(this.beaconConfig.purchase?.trigger || '');
    const cartItems = document.querySelectorAll(this.getProductSelector(this.beaconConfig.purchase.productPath));
    const queryProductsDetails = _.map(queryProducts, (product) => this.getRetailProductDetails(product, this.beaconConfig.query.fields))
    const cartItemDetails = _.map(cartItems, (product) => this.getRetailProductDetails(product, this.beaconConfig.purchase.fields))

    console.log(`=== 📡 Beacon Debug Mode Enabled 📡 ====`);
    console.log(`📝 This Beacon info can be reported via "window.logBeaconInfo()"`, '\n');
    console.log(`❇ Beacon Config:`, this.beaconConfig);
    console.log(`❇ Query signal handler:`, this.customSignalHandlers.query);
    console.log(`❇ Click signal handler:`, this.customSignalHandlers.click);
    console.log(`❇ Add to cart signal handler:`, this.customSignalHandlers.cartAdd);
    console.log(`❇ Purchase signal handler:`, this.customSignalHandlers.purchase);
    console.log(`❇ Add to cart triggers:`, cartAddElements);
    console.log(`❇ Purchase triggers:`, purchaseElements);
    console.log(`❇ Products in main query list:`, queryProducts, 'Product Details:', queryProductsDetails);
    console.log(`❇ Products in cart:`, cartItems, 'Cart Item Details:', cartItemDetails);

    if (this.isLocalMode) {
      console.log(`\n=== 📌 Beacon Local Mode Enabled 📌 ====`);
      console.log(`❇ API URL:`, localStorage[this.localApiUrlKey] || this.defaultLocalApiUrl);
      console.log(`❇ Customer ID:`, localStorage[this.localCustomerIdKey]);
    }

    console.log(`\n\n🔩 To enable local mode sending signals to localhost for backend local dev purposes:\n\n`);
    console.log(`localStorage.${this.localModeKey} = 'true'`);
    console.log(`localStorage.${this.localCustomerIdKey} = __customer_id__\n\n`);
    console.log(`/** Default to ${this.defaultLocalApiUrl} */`);
    console.log(`localStorage.${this.localApiUrlKey} = __local_api_host__`);
  }
}

export const beaconAgent = new BeaconAgent();